import _ from "lodash";
import { useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import apiAgent from "../../api/apiAgent";
import { useNavigate, useParams } from "react-router";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { useAtomValue } from "jotai";
import {
  hostDataAtom,
  modelDataAtom,
  useModifyUploadList,
  userAtom,
} from "../../atoms";
import {
  BackButton,
  Button,
  DeleteButton,
  EditButton,
} from "../../components/Button";
import { Link, useSearchParams } from "react-router-dom";
import { getDataAndSchemaFromModel } from "../../components/Forms/utils";
import { DisplayValue, UserNameDisplay } from "../../components/DisplayValue";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, pause, useOriginParams } from "../../utils";
import { useSetChangesMade } from "../../components/Publishing/utils";
import { WorkOrPhotosPreview } from "./previews/WorkOrPhotosPreview";
import { ShowBanner } from "../../components/ShowBanner";
import {
  getImageThumbnailFromURL,
  getThumbFilename,
  uploadFile,
} from "../../components/Forms/FileHandling/Utils";
import { Spinner } from "../../components/Loader";

const alwaysShownFields = [
  { name: "updatedAt", type: "time" },
  { name: "createdAt", type: "time" },
];

const ThumbnailDisplay = ({
  itemData,
  modelName,
}: {
  itemData: any;
  modelName: string;
}) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const hostData = useAtomValue(hostDataAtom);
  const userData = useAtomValue(userAtom);
  const setChangesMade = useSetChangesMade(modelName);
  const [addThumbToList, removeThumbFromList] = useModifyUploadList(
    `${itemData.id}_thumb`
  );

  useEffect(() => {
    if (thumbFile) startThumbUpload(thumbFile);
  }, [thumbFile]);

  const generateThumb = async () => {
    try {
      setIsGenerating(true);
      // Get imageUrl and throw if not valid
      const baseUrl = itemData?.imageUrl || null;
      if (!baseUrl || !_.isString(baseUrl)) {
        throw `Invalid imageUrl: ${baseUrl}`;
      }
      const hackUrl = `${baseUrl}?${Math.random()}`; // This is to get around Chrome's CORS issue
      getImageThumbnailFromURL(
        hackUrl,
        getThumbFilename(baseUrl),
        setThumbFile,
        true
      );
    } catch (err) {
      console.log(err);
      tMsg.warning("Error generating thumbnail");
    } finally {
      setIsGenerating(false);
    }
  };

  const startThumbUpload = async (selectedFile: File | null) => {
    try {
      if (selectedFile === null) {
        tMsg.error("Unable to upload selected file");
        return;
      }
      setIsGenerating(true);
      addThumbToList();
      uploadFile(
        selectedFile,
        (location) => {
          tMsg.info("Thumb upload complete");
          saveItem(location);
        },
        hostData.awsBucket,
        hostData.awsRegion
      );
    } catch (err) {
      tMsg.warning("Error uploading thumbnail. See console for details");
      console.log(err);
    } finally {
      removeThumbFromList();
      setIsGenerating(false);
    }
  };

  const saveItem = async (thumbLocation: string) => {
    try {
      setIsGenerating(true);
      const response = await apiAgent.DataType.update(modelName, {
        ...itemData,
        targetIdx: itemData.id,
        editedBy: userData?.id,
        thumbnail: thumbLocation,
      });
      if (response.status === 200) {
        tMsg.success(`Thumbnail successfully updated`);
        setChangesMade();
        navigate(0);
      } else throw response.statusText;
    } catch (err) {
      tMsg.error(`Error updating thumbnail information`);
      console.log(err);
    } finally {
      setIsGenerating(false);
    }
  };

  // console.log(itemData.imageUrl);
  const displayValue = itemData.thumbnail ? (
    <div className="flex gap-x-2">
      <div>{itemData.thumbnail}</div>
      {isGenerating ? (
        <Spinner />
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            generateThumb();
          }}
          disabled={isGenerating}
          className="hover:opacity-70 underline text-xs"
        >
          Regenerate
        </button>
      )}
    </div>
  ) : itemData.imageUrl ? (
    <Button
      onClick={(e) => {
        e.preventDefault();
        generateThumb();
      }}
      disabled={isGenerating}
      className="py-0.5 px-2"
    >
      {isGenerating ? <Spinner /> : `Regenerate thumb`}
    </Button>
  ) : (
    <div className="text-gray-400">Upload an image to add a thumbnail</div>
  );

  return (
    <div className="flex text-sm font-medium">
      <div className="min-w-[10rem]">Thumbnail:</div>
      {displayValue}
    </div>
  );
};

const WorkPhotosShow = (): JSX.Element => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const screenId = _.toNumber(searchParams.get("screenType") || -1);
  const [originFrom, originId] = useOriginParams();
  const modelName = params.model?.toString() || "unknown";
  const setChanges = useSetChangesMade(modelName);

  const modelData = useAtomValue(modelDataAtom);
  const [schema, setSchema] = useState<any[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState<boolean>(true);
  const [itemData, setItemData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    if (modelData && modelData !== null && isLoadingSchema) {
      const [, getSchema] = getDataAndSchemaFromModel(
        modelData,
        modelName,
        true
      );

      // we don't set item data for update
      // console.log(getSchema);
      setSchema([...getSchema, ...alwaysShownFields]);
      setIsLoadingSchema(false);
    }
  }, [modelData, modelName]);

  const fetchData = async () => {
    try {
      const response = await apiAgent.DataType.getAll(modelName);

      const { data } = response;
      const item =
        data.find((item: any) => _.toString(item.id) === params.id) || null;

      setItemData(item);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePressed = async () => {
    try {
      if (!params.id) throw "No ID specified.";
      const response = await apiAgent.DataType.delete(modelName, {
        targetIdx: _.toNumber(params.id),
      });
      if (response.status === 200) {
        tMsg.success(`Slide deleted`);
        setChanges();
        if (originFrom && originId)
          navigate(`/m/${originFrom}/show/${originId}`);
        else navigate(`/m/${modelName}`);
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting slide`);
      console.log(err);
    }
  };

  const handlePublishPressed = async () => {
    try {
      if (!params.id) throw "No ID specified";
      setIsPublishing(true);
      await pause(300);
      const time = new Date();
      const published = !itemData.published || false;
      const response = await apiAgent.DataType.update(modelName, {
        ...itemData,
        published,
        publishedAt: time,
        updatedAt: time,
        targetIdx: itemData.id,
      });
      if (response.status === 200) {
        tMsg.success(
          published ? `Slide set to visible` : `Slide set to hidden`
        );
        fetchData();
      }
    } catch (err) {
      tMsg.error(`Error toggling visibility`);
      console.log(err);
    } finally {
      setIsPublishing(false);
    }
  };

  const crumbs = [
    {
      label: checkTitle(modelName),
      link: `/m/${modelName}`,
    },
  ];

  const hideField = itemData?.type === "Video" ? "imageUrl" : "videoUrl";
  const filteredSchema = schema.filter(
    (f) =>
      !_.includes(
        [
          hideField,
          "editedBy",
          "published",
          "publishedAt",
          "type",
          "thumbnail",
        ],
        f.name
      )
  );

  return (
    <PageContainer
      isLoading={isLoading || isLoadingSchema}
      className="relative"
    >
      <PageContent reduceHeightForFooter>
        <BreadCrumbs crumbs={crumbs} />
        {
          // TODO: Create a better display if id/itemData is invalid
          params.id && itemData !== null ? (
            <>
              <TitleBar title={checkTitle(modelName)}>
                <BackButton to={`/m/${modelName}`} />
              </TitleBar>
              {itemData && (
                <ShowBanner
                  isPublishing={isPublishing}
                  handlePublishPressed={handlePublishPressed}
                  published={itemData.published}
                />
              )}
              <div className="flex flex-col gap-4">
                <UserNameDisplay id={itemData.editedBy} />
                <DisplayValue
                  name="Status"
                  value={itemData.published === true ? "Visible" : "Hidden"}
                />
                {filteredSchema.map((item, index) => (
                  <DisplayValue
                    key={index}
                    name={item.name}
                    model={modelName}
                    value={itemData[item.name]}
                    type={item.type}
                  />
                ))}
                {/* <ThumbnailDisplay itemData={itemData} modelName={modelName} /> */}
              </div>
            </>
          ) : (
            <TitleBar title={`Item ${params.id} not found`} />
          )
        }
        <WorkOrPhotosPreview itemData={itemData} />
      </PageContent>
      <PageFooterBar>
        <Link to={`/m/${modelName}/edit/${params.id}?screenType=${screenId}`}>
          <EditButton />
        </Link>
        <DeleteButton onClick={() => handleDeletePressed()} />
      </PageFooterBar>
    </PageContainer>
  );
};

export default WorkPhotosShow;
